import { useState } from "react";
import {
  Inbox,
  TriangleAlert,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Info,
  Sliders,
  PanelRight,
  Maximize2,
  Check,
  RefreshCw,
  Bell,
  Search,
  PackageCheck,
  SlidersHorizontal,
} from "lucide-react";
import {
  EmptyState,
  ErrorState,
  Skeleton,
  Loading,
  Spinner,
  LoadingOverlay,
  Drawer,
  Modal,
  type ModalSize,
  type ModalIconTone,
  type ModalIconVariant,
  Button,
  Badge,
  Input,
  useToast,
} from "@/shared/ui";
import { FormField } from "@/shared/ui/FormField";

export function TestUiCoreDataStates() {
  const { toast } = useToast();

  // Loading Demo Overlay State
  const [isDemoOverlayActive, setIsDemoOverlayActive] = useState(false);

  // 1. FormField Demo State
  const [formFieldValue, setFormFieldValue] = useState("");
  const [showError, setShowError] = useState(false);

  // 2. Empty / Error States Refresh Counter
  const [retryCount, setRetryCount] = useState(0);

  // 3. Drawer State
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerSize, setDrawerSize] = useState<"sm" | "md" | "lg">("md");

  // 4. Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalSize, setModalSize] = useState<ModalSize>("medium");
  const [customWidth, setCustomWidth] = useState("720px");
  const [modalIconVariant, setModalIconVariant] = useState<ModalIconVariant>("badge");
  const [modalIconTone, setModalIconTone] = useState<ModalIconTone>("primary");

  return (
    <div className="space-y-6">
      {/* 1. FormField Architecture */}
      <section className="rounded-control border border-border bg-surface p-5 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border/60 pb-3 mb-4">
          <div>
            <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
              <CheckCircle2 className="size-5 text-primary" />
              1. Khung Bọc FormField Tiêu Chuẩn (FormField)
            </h3>
            <p className="mt-1 mb-0 text-caption text-text-muted">
              Wrapper chuẩn mực cho toàn bộ hệ thống form: Tự động gắn nhãn (Label), đánh dấu bắt buộc (*), hiển thị ghi chú hint hoặc thông báo lỗi đỏ kèm a11y.
            </p>
          </div>
          <Button
            size="sm"
            variant={showError ? "danger" : "secondary"}
            onClick={() => setShowError(!showError)}
          >
            {showError ? "Tắt báo lỗi (Ẩn Error)" : "Bật thử báo lỗi (Hiện Error)"}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-control border border-border bg-bg p-4">
            <FormField
              label="Mã định danh lô thành phẩm"
              required
              hint="Quy cách chuẩn: LK-YYYY-XXX (ví dụ: LK-2026-001)"
              error={showError ? "Mã lô này đã tồn tại trong ca sản xuất hiện tại!" : undefined}
            >
              <Input
                placeholder="Nhập mã lô (VD: LK-2026-001)..."
                value={formFieldValue}
                onChange={(e) => setFormFieldValue(e.target.value)}
                tone={showError ? "error" : "default"}
                aria-label="Mã định danh lô thành phẩm"
              />
            </FormField>
          </div>

          <div className="rounded-control border border-border bg-bg p-4 space-y-2">
            <div className="text-caption font-semibold text-text-muted uppercase tracking-wider">
              Quy chuẩn thiết kế FormField trong TM Food
            </div>
            <ul className="text-caption text-text-secondary space-y-1.5 pl-4 m-0 list-disc">
              <li>
                <strong className="text-text">Label:</strong> Font semibold, màu text tiêu chuẩn, có dấu hoa thị đỏ <code className="text-danger-fg">*</code> nếu <code className="font-mono">required</code>.
              </li>
              <li>
                <strong className="text-text">Hint:</strong> Hiển thị chữ nhỏ màu mờ dưới input khi hợp lệ để hướng dẫn người thao tác.
              </li>
              <li>
                <strong className="text-text">Error:</strong> Tự động thay thế hint bằng thông báo lỗi màu đỏ khi có sự cố xác thực dữ liệu.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* 2. Toast Notifications */}
      <section className="rounded-control border border-border bg-surface p-5 shadow-xs">
        <div className="border-b border-border/60 pb-3 mb-4">
          <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
            <Bell className="size-5 text-primary" />
            2. Thông Báo Nổi (Toast Notification System)
          </h3>
          <p className="mt-1 mb-0 text-caption text-text-muted">
            Hệ thống thông báo toàn cục với hook <code className="font-mono text-primary">useToast()</code>. Bấm các nút dưới đây để kích hoạt thông báo thật ở góc phải trên cùng màn hình.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            variant="primary"
            onClick={() =>
              toast({
                tone: "success",
                title: "Thao tác thành công",
                message: "Đã lưu bản ghi kiểm nghiệm chất lượng #KCS-009.",
              })
            }
          >
            Toast Thành Công (Success)
          </Button>

          <Button
            size="sm"
            variant="danger"
            onClick={() =>
              toast({
                tone: "error",
                title: "Lỗi lưu dữ liệu",
                message: "Không thể kết nối đến máy chủ MES. Vui lòng kiểm tra mạng.",
              })
            }
          >
            Toast Lỗi (Error)
          </Button>

          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              toast({
                tone: "warning",
                title: "Cảnh báo nhiệt độ",
                message: "Kho đông #2 đang vượt ngưỡng -16°C. Cần kiểm tra dàn lạnh.",
              })
            }
          >
            Toast Cảnh Báo (Warning)
          </Button>

          <Button
            size="sm"
            variant="soft"
            onClick={() =>
              toast({
                tone: "info",
                title: "Đồng bộ IoT",
                message: "Hệ thống đang tải dữ liệu đo từ 24 cảm biến phân xưởng.",
              })
            }
          >
            Toast Thông Tin (Info)
          </Button>
        </div>
      </section>

      {/* 3. Empty & Error States */}
      <section className="rounded-control border border-border bg-surface p-5 shadow-xs">
        <div className="border-b border-border/60 pb-3 mb-4">
          <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
            <Sliders className="size-5 text-primary" />
            3. Trạng Thái Dữ Liệu (EmptyState & ErrorState)
          </h3>
          <p className="mt-1 mb-0 text-caption text-text-muted">
            Xử lý trải nghiệm người dùng khi bảng không có dữ liệu hoặc API phát sinh lỗi kết nối.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Empty State */}
          <div className="rounded-control border border-dashed border-border bg-bg/50 p-3">
            <EmptyState
              icon={<Inbox className="size-6 text-text-muted" />}
              title="Không có phiếu kiểm nghiệm"
              description="Chưa có dữ liệu kiểm định nào trong ca làm việc này. Bạn có thể bấm tạo phiếu mới để bắt đầu."
              action={
                <Button size="sm" variant="primary">
                  Tạo Phiếu Kiểm Nghiệm Mới
                </Button>
              }
            />
          </div>

          {/* Error State */}
          <div className="rounded-control border border-dashed border-danger/40 bg-danger-soft/10 p-3">
            <ErrorState
              title="Không thể tải danh sách lô"
              description="Kết nối đến cơ sở dữ liệu kho tạm thời gián đoạn. Vui lòng bấm thử lại để đồng bộ."
              onRetry={() => {
                setRetryCount((c) => c + 1);
                toast({
                  tone: "info",
                  message: `Đang thử tải lại dữ liệu lần thứ ${retryCount + 1}...`,
                });
              }}
              retryLabel="Thử Kết Nối Lại"
            />
          </div>
        </div>
      </section>

      {/* 4. Skeleton Shimmer Loading */}
      <section className="rounded-control border border-border bg-surface p-5 shadow-xs">
        <div className="border-b border-border/60 pb-3 mb-4">
          <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
            <Loader2 className="size-5 text-primary" />
            4. Hiệu Ứng Chờ Tải (Skeleton Shimmer)
          </h3>
          <p className="mt-1 mb-0 text-caption text-text-muted">
            Bộ khung xương chuyển động sóng (pulse) giúp người dùng hình dung trước cấu trúc nội dung trong lúc chờ API trả về dữ liệu.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Table Skeleton */}
          <div className="space-y-2 rounded-control border border-border bg-bg p-4">
            <span className="text-caption font-semibold text-text-secondary">Khung Bảng (Table Variant):</span>
            <Skeleton variant="table" rows={3} />
          </div>

          {/* Card Skeleton */}
          <div className="space-y-2 rounded-control border border-border bg-bg p-4">
            <span className="text-caption font-semibold text-text-secondary">Khung Thẻ (Card Variant):</span>
            <Skeleton variant="card" />
          </div>

          {/* Circle + Text Skeleton */}
          <div className="space-y-2 rounded-control border border-border bg-bg p-4">
            <span className="text-caption font-semibold text-text-secondary">Khung Hồ Sơ (Circle + Text):</span>
            <div className="flex items-center gap-3">
              <Skeleton variant="circle" />
              <div className="flex-1 space-y-1.5">
                <Skeleton rows={2} />
              </div>
            </div>
          </div>
        </div>

        {/* 4.2 Component Loading & Spinner Suite */}
        <div className="mt-5 pt-4 border-t border-border/60 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="m-0 text-caption font-bold uppercase tracking-wider text-text">
                Bộ Component Loading, Spinner & LoadingOverlay
              </h4>
              <p className="m-0 mt-0.5 text-caption text-text-muted">
                Các khối hiển thị tiến trình quay chuẩn Accessibility (`role=&quot;status&quot;`) cho Button, Card, Table và Modal.
              </p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              leftIcon={<RefreshCw className="size-3.5" />}
              onClick={() => {
                setIsDemoOverlayActive(true);
                setTimeout(() => setIsDemoOverlayActive(false), 2000);
              }}
            >
              Demo LoadingOverlay (2s)
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Spinners */}
            <div className="space-y-2.5 rounded-control border border-border bg-bg p-4">
              <span className="text-caption font-semibold text-text-secondary">Icon Spinner Các Kích Thước:</span>
              <div className="flex items-center gap-3 pt-1">
                <div className="flex flex-col items-center gap-1">
                  <Spinner size="xs" />
                  <span className="text-[10px] text-text-muted font-mono">xs</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <Spinner size="sm" tone="primary" />
                  <span className="text-[10px] text-text-muted font-mono">sm</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <Spinner size="md" tone="warning" />
                  <span className="text-[10px] text-text-muted font-mono">md</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <Spinner size="lg" tone="danger" />
                  <span className="text-[10px] text-text-muted font-mono">lg</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <Spinner size="xl" tone="success" />
                  <span className="text-[10px] text-text-muted font-mono">xl</span>
                </div>
              </div>
            </div>

            {/* Loading with Labels */}
            <div className="space-y-2.5 rounded-control border border-border bg-bg p-4">
              <span className="text-caption font-semibold text-text-secondary">Loading Có Nhãn Văn Bản:</span>
              <div className="space-y-2 pt-1">
                <Loading size="sm" label="Đang đồng bộ cơ sở dữ liệu..." />
                <div className="pt-2 border-t border-border/50">
                  <Loading size="md" label="Đang xử lý xuất kho…" tone="primary" />
                </div>
              </div>
            </div>

            {/* Interactive LoadingOverlay Preview */}
            <div className="relative overflow-hidden rounded-control border border-border bg-surface p-4 min-h-[110px] flex flex-col justify-between">
              <LoadingOverlay
                visible={isDemoOverlayActive}
                label="Đang nạp dữ liệu..."
              />
              <span className="text-caption font-semibold text-text">Thẻ Dữ Liệu Demo Overlay</span>
              <p className="m-0 text-caption text-text-muted">
                Bấm nút &quot;Demo LoadingOverlay&quot; để quan sát lớp phủ mờ tinh tế và spinner trung tâm.
              </p>
              <div className="flex justify-end pt-2">
                <Badge tone="primary" label="Ready" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Drawer & Modal Overlays */}
      <section className="rounded-control border border-border bg-surface p-5 shadow-xs">
        <div className="border-b border-border/60 pb-3 mb-4">
          <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
            <Maximize2 className="size-5 text-primary" />
            5. Khay Trượt (Drawer) & Hộp Thoại (Modal Dialog)
          </h3>
          <p className="mt-1 mb-0 text-caption text-text-muted">
            Các lớp phủ tương tác chuẩn Radix UI với cơ chế bẫy tiêu điểm (focus trap), đóng bằng phím Escape và click ngoài thông minh.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Button
              size="md"
              variant="primary"
              onClick={() => setIsDrawerOpen(true)}
              className="inline-flex items-center gap-2"
            >
              <PanelRight className="size-4" />
              Mở Drawer (Khay Trượt Phải)
            </Button>
            <div className="flex items-center gap-1">
              {(["sm", "md", "lg"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setDrawerSize(s)}
                  className={`px-2 py-1 text-caption rounded cursor-pointer ${
                    drawerSize === s
                      ? "bg-primary text-on-primary font-bold"
                      : "bg-bg text-text-secondary hover:bg-border/60"
                  }`}
                >
                  {s.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="md"
              variant="secondary"
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center gap-2"
            >
              <Maximize2 className="size-4" />
              Mở Modal (Hộp Thoại Giữa)
            </Button>
            <div className="flex flex-wrap items-center gap-1">
              {(
                [
                  { key: "small", label: "Small (512px)" },
                  { key: "medium", label: "Medium (672px)" },
                  { key: "large", label: "Large (896px)" },
                  { key: "custom", label: "Custom (720px)" },
                ] as const
              ).map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setModalSize(s.key)}
                  className={`px-2 py-1 text-caption rounded cursor-pointer transition-colors ${
                    modalSize === s.key
                      ? "bg-primary text-on-primary font-bold shadow-xs"
                      : "bg-bg text-text-secondary hover:bg-border/60"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Live Drawer Instance */}
        <Drawer
          open={isDrawerOpen}
          onOpenChange={setIsDrawerOpen}
          title="Bộ Lọc & Tra Cứu Chuyên Sâu"
          icon={<SlidersHorizontal className="size-4" />}
          iconVariant="badge"
          iconTone="primary"
          description="Thiết lập các tiêu chí lọc nâng cao cho dữ liệu quản trị sản xuất."
          size={drawerSize}
          footer={
            <div className="flex items-center justify-end gap-2 w-full">
              <Button size="sm" variant="secondary" onClick={() => setIsDrawerOpen(false)}>
                Đóng
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  setIsDrawerOpen(false);
                  toast({ tone: "success", message: "Đã áp dụng bộ lọc nâng cao từ Drawer." });
                }}
              >
                Áp Dụng Bộ Lọc
              </Button>
            </div>
          }
        >
          <div className="space-y-4 py-2">
            <FormField label="Từ khóa tìm kiếm nhanh">
              <Input placeholder="Tìm theo tên sản phẩm, mã phiếu..." />
            </FormField>
            <FormField label="Phân xưởng sản xuất">
              <Input defaultValue="Phân xưởng Chế biến 2" />
            </FormField>
            <div className="rounded-control border border-border bg-bg p-3 text-caption text-text-secondary">
              Drawer kích thước hiện tại: <strong className="font-mono text-primary">{drawerSize.toUpperCase()}</strong>. Hỗ trợ cuộn độc lập khi nội dung dài.
            </div>
          </div>
        </Drawer>

        {/* Live Modal Instance */}
        <Modal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          title="Xác Nhận Xuất Xưởng Lô Hàng"
          icon={<PackageCheck className="size-4" />}
          iconVariant={modalIconVariant}
          iconTone={modalIconTone}
          description="Hệ thống sẽ đồng bộ thông tin lô hàng vào sổ cái kho thành phẩm và gửi thông báo tới bộ phận QA."
          size={modalSize}
          customWidth={modalSize === "custom" ? customWidth : undefined}
          footer={
            <div className="flex items-center justify-end gap-2 w-full">
              <Button size="sm" variant="secondary" onClick={() => setIsModalOpen(false)}>
                Hủy Bỏ
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  setIsModalOpen(false);
                  toast({ tone: "success", message: "Đã phê duyệt xuất xưởng thành công!" });
                }}
              >
                Xác Nhận Xuất Xưởng
              </Button>
            </div>
          }
        >
          <div className="space-y-3 py-2 text-body text-text-secondary">
            {/* Tùy chỉnh iconVariant & iconTone */}
            <div className="rounded-control border border-primary/20 bg-primary-soft/30 p-3 space-y-2 text-caption">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-text">Kiểu Icon Header (iconVariant):</span>
                <div className="inline-flex rounded-control bg-surface p-0.5 border border-border">
                  {(["badge", "inline"] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setModalIconVariant(v)}
                      className={`px-2.5 py-0.5 text-[11px] rounded font-medium cursor-pointer transition-colors ${
                        modalIconVariant === v
                          ? "bg-primary text-on-primary font-bold shadow-xs"
                          : "text-text-muted hover:text-text"
                      }`}
                    >
                      {v === "badge" ? "Badge (Khung bo)" : "Inline (Trần)"}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-text">Tone Màu Icon (iconTone):</span>
                <div className="inline-flex flex-wrap rounded-control bg-surface p-0.5 border border-border gap-0.5">
                  {(["primary", "info", "success", "warning", "danger", "neutral"] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setModalIconTone(t)}
                      className={`px-2 py-0.5 text-[11px] rounded font-medium cursor-pointer transition-colors ${
                        modalIconTone === t
                          ? "bg-primary text-on-primary font-bold shadow-xs"
                          : "text-text-muted hover:text-text"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-control border border-border bg-bg p-3 text-caption">
              <div>
                Kích thước tiêu chuẩn hiện tại: <strong className="font-mono text-primary uppercase">{modalSize}</strong>
                {modalSize === "small" && " (512px - phù hợp xác nhận nhanh, cảnh báo, 1 trường)"}
                {modalSize === "medium" && " (672px - chuẩn dialog đơn, form cơ bản 1-2 cột)"}
                {modalSize === "large" && " (896px - form tạo/sửa 2 cột, nhiều trường thông tin)"}
                {modalSize === "custom" && ` (${customWidth} - tùy biến linh hoạt theo nhu cầu)`}
              </div>
            </div>
            <div className="flex items-center gap-1.5 pt-1">
              <span className="text-caption text-text-muted font-medium">Thử đổi kích thước:</span>
              {(
                [
                  { id: "small", label: "SMALL (512px)" },
                  { id: "medium", label: "MEDIUM (672px)" },
                  { id: "large", label: "LARGE (896px)" },
                  { id: "custom", label: "CUSTOM" },
                ] as const
              ).map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setModalSize(s.id)}
                  className={`px-2 py-0.5 text-[11px] rounded cursor-pointer transition-colors ${
                    modalSize === s.id
                      ? "bg-primary text-on-primary font-semibold"
                      : "bg-surface border border-border text-text hover:bg-bg"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <p className="m-0 pt-1">
              Bạn đang chuẩn bị xuất kho lô hàng <strong className="text-text font-mono">LK-2026-001</strong> với tổng số lượng <strong className="text-primary font-mono">1.250 kg</strong> fillet cá tra đông lạnh.
            </p>
            <div className="rounded-control border border-info/40 bg-info-soft/30 p-3 text-caption text-info-fg">
              Lưu ý: Sau khi xác nhận, trạng thái lô hàng sẽ chuyển sang <strong>Đã hoàn thành</strong> và không thể hoàn tác trực tiếp.
            </div>
          </div>
        </Modal>
      </section>
    </div>
  );
}
